import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { IResponseHandlerOptions, useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateChannelDistribution,
  IUpdateChannelDistribution,
} from './types';
import { IDistributionResponse, ITemplatesLightListItem } from '../types';

export const useGetChannelDistributionTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['channel-distribution-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ChannelDistributionSectionTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetChannelDistributionTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useGetChannelDistributionSectionTemplate =  (
  id: string,
  options?: IResponseHandlerOptions<IDistributionResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IDistributionResponse, DefaultError>({
    queryKey: ['channel-distribution-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `ChannelDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetChannelDistributionSectionTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostChannelDistributionTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostChannelDistributionTemplate'
  );
  return useMutation<string, DefaultError, ICreateChannelDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'ChannelDistributionSectionTemplate',
        body: { channelDistributionSectionTemplate: body },
      });
      
      return responseHandler(response, {
        groupName: 'usePostChannelDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutChannelDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutChannelDistributionTemplate'
  );
  return useMutation<string, DefaultError, IUpdateChannelDistribution>({
    async mutationFn(body) {
      loading(
        i18next.t('service-message.create.loading', {
          what: body.title,
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'ChannelDistributionSectionTemplate',
        body: {channelDistributionSectionId: id, channelDistributionSectionTemplate: body},
      });

      return responseHandler(response, {
        groupName: 'usePutChannelDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteChannelDistributionTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteChannelDistributionTemplate'
  );
  return useMutation<string, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `ChannelDistributionSectionTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteChannelDistributionTemplate',
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
